/**
 * Genus - core first-party library, entry point.
 *
 * Bundled by `npm run build:libs` (esbuild) into dist/genus.js (+ .min.js)
 * and loaded before app.js in partials/scripts.latte. Add new modules as files next to
 * this one and wire them up here.
 *
 * Framework-free, no dependencies. Everything is exposed under the single
 * global namespace `g` (canonical alias: `window.Genus`), e.g. g.success(...).
 * API reference: docs/libs/genus.md - keep it in sync when adding methods.
 */
import { VERSION, settings, init, esc } from './core.js';
import { notify, success, error, warning, info } from './notify.js';
import { shake } from './shake.js';
import { outcome } from './outcome.js';
import { apiFetch } from './fetch.js';
import { translate, setTranslations, DEFAULT_LANG } from './translate.js';
import { fieldError } from './field.js';
import { busy } from './busy.js';
import { format } from './format.js';
import { shortcuts, initShortcuts } from './shortcuts.js';
import { route } from './route.js';
import { userData } from './userdata.js';
import { logError, initClientLog } from './log.js';
import { pdf } from './pdf.js';
import { action, initActionDetector } from './action.js';
import { segmented, initSegmented } from './segmented.js';
import { webauthn } from './webauthn.js';
import { copy, initCopy } from './copy.js';

const g = window.g || {};

Object.assign(g, { settings, notify, success, error, warning, info, shake, outcome, fetch: apiFetch, pdf, translate, setTranslations, fieldError, busy, action, segmented, webauthn, copy, format, esc, shortcuts, route, userData, logError });
initShortcuts();
g.version = () => VERSION;

// Registry of loaded first-party libraries. Each library calls
// g.registerLib('<name>') when it loads; g.libs() lists them.
const loadedLibs = ['genus'];
g.registerLib = (name) => { if (!loadedLibs.includes(name)) loadedLibs.push(name); };
g.libs = () => [...loadedLibs];
g.lang = g.lang || DEFAULT_LANG;
g.user = g.user || null;
g.init = (newSettings) => {
	init(newSettings);
	if (settings.lang) g.lang = settings.lang;
	// the session user (name, email, avatar, data) — null when anonymous
	if ('user' in settings) g.user = settings.user;
	// uncaught-error reporting to /frm/log/client (docs/libs/genus.md):
	// attached once; settings.clientLog === false silences it at report time
	initClientLog();
	// the unguarded-action detector (docs/libs/genus.md "g.action"): a dev
	// environment flags a click that starts async work without g.action
	if (settings.dev === true) initActionDetector();
	// the segmented controls on the page get their sliding thumb
	initSegmented();
	// the [copy] / [copy-target] buttons (docs/libs/genus.md "Copy to clipboard")
	initCopy();
	return g;
};

window.g = g;
window.Genus = g;
